/**
 * Waiting Room – shared UI for Doctor and User
 * Designed to match the exact modern Fitnova Doctor & User Waiting Room layout mockups.
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../api/axios';
import UserNavbar from '../components/UserNavbar';
import DoctorNavbar from '../components/DoctorNavbar';
import { toast } from 'react-toastify';

function WaitingRoom() {
  const { appointmentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isDoctor = location.pathname.includes('/doctor/');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sessionInfo, setSessionInfo] = useState(null);
  const [timeUntilStart, setTimeUntilStart] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [canJoin, setCanJoin] = useState(false);
  const [sessionActive, setSessionActive] = useState(false);
  const [activeTab, setActiveTab] = useState('All Updates');
  const [mediaTestStatus, setMediaTestStatus] = useState('');

  // Admin health tips/posts/videos (waitingroom app)
  const [healthContents, setHealthContents] = useState([]);
  const [healthLoading, setHealthLoading] = useState(false);
  const [healthError, setHealthError] = useState('');
  const [showAllHealthPosts, setShowAllHealthPosts] = useState(false);

  const TIP_STYLES = [
    { icon: 'bi-droplet-fill', bg: '#E8F1FF', color: '#2563EB' },
    { icon: 'bi-basket2-fill', bg: '#E8F8EF', color: '#16A34A' },
    { icon: 'bi-person-walking', bg: '#FFF1E8', color: '#EA580C' },
    { icon: 'bi-moon-stars-fill', bg: '#F3E8FF', color: '#7C3AED' },
    { icon: 'bi-flower1', bg: '#FCE7F3', color: '#DB2777' },
  ];

  const normalizeHealthItem = (c) => ({
    id: c.id,
    title: c.title || 'Health tip',
    description: (c.description || c.short_content || '').trim(),
    content_type: (c.content_type || 'POST').toUpperCase(),
    image: c.image || null,
    video: c.video || null,
    created_at: c.created_at || null,
  });

  const fetchHealthContents = useCallback(async () => {
    try {
      setHealthLoading(true);
      setHealthError('');
      const res = await api.get('waiting-room/health-contents/');
      const raw = res.data;
      const list = Array.isArray(raw)
        ? raw
        : (Array.isArray(raw?.results)
          ? raw.results
          : (Array.isArray(raw?.health_content) ? raw.health_content : []));
      setHealthContents(list.map(normalizeHealthItem));
    } catch (err) {
      console.error('Health tips fetch failed:', err);
      setHealthError(err?.response?.data?.error || 'Failed to load health tips');
      setHealthContents([]);
    } finally {
      setHealthLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHealthContents();
  }, [fetchHealthContents, appointmentId]);

  // Also pick up tips from session-info if dedicated API was empty
  useEffect(() => {
    if (healthContents.length > 0) return;
    const fromSession = sessionInfo?.healthcare_posts;
    if (Array.isArray(fromSession) && fromSession.length > 0) {
      setHealthContents(fromSession.map(normalizeHealthItem));
    }
  }, [sessionInfo, healthContents.length]);


  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  const resolveMediaUrl = (pathOrUrl) => {
    if (!pathOrUrl) return null;
    if (typeof pathOrUrl === 'string' && (pathOrUrl.startsWith('http://') || pathOrUrl.startsWith('https://'))) {
      return pathOrUrl;
    }
    if (typeof pathOrUrl === 'string' && pathOrUrl.startsWith('/media/')) {
      return `${apiUrl}${pathOrUrl}`;
    }
    if (typeof pathOrUrl === 'string') {
      return `${apiUrl}/media/${pathOrUrl}`;
    }
    return null;
  };

  // Prefer dedicated API list; fallback to session-info healthcare_posts
  const allHealthContents = (() => {
    if (healthContents.length > 0) return healthContents;
    const fromSession = sessionInfo?.healthcare_posts;
    if (Array.isArray(fromSession) && fromSession.length > 0) {
      return fromSession.map(normalizeHealthItem);
    }
    return [];
  })();

  const filteredHealthContents = allHealthContents.filter((c) => {
    if (!c?.content_type) return false;
    if (activeTab === 'Health Tips') return c.content_type === 'POST';
    if (activeTab === 'Videos') return c.content_type === 'VIDEO';
    if (activeTab === 'Announcements') return c.content_type === 'POST';
    return true; // All Updates
  });

  const visibleHealthContents = showAllHealthPosts
    ? filteredHealthContents
    : filteredHealthContents.slice(0, 2);

  const fetchSessionInfo = useCallback(async () => {
    try {
      const res = await api.get(`video/session-info/${appointmentId}/`);
      const data = res.data;
      setSessionInfo(data);
      setTimeUntilStart(data.time_until_start);
      setTimeRemaining(data.time_remaining);
      setCanJoin(data.can_join);
      setSessionActive(data.session_active);
      setLoading(false);
      if (data.slot_ended || data.time_remaining <= 0) {
        setError('The scheduled slot time has ended');
        setTimeout(() => {
          navigate(isDoctor ? '/doctor/appointments' : '/user/bookings');
        }, 3000);
      }
    } catch (err) {
      setLoading(false);
      setError(err.response?.data?.error || 'Failed to load session information');
    }
  }, [appointmentId, isDoctor, navigate]);

  useEffect(() => {
    fetchSessionInfo();
    const pollInterval = setInterval(fetchSessionInfo, 5000);
    return () => clearInterval(pollInterval);
  }, [fetchSessionInfo]);

  useEffect(() => {
    if (timeUntilStart <= 0 && timeRemaining <= 0) return;
    const timer = setInterval(() => {
      setTimeUntilStart(prev => {
        const next = Math.max(0, prev - 1);
        if (next === 0 && prev > 0) {
          setCanJoin(true);
        }
        return next;
      });
      setTimeRemaining(prev => {
        const next = Math.max(0, prev - 1);
        if (next === 0 && prev > 0) {
          setError('The scheduled slot time has ended');
          setTimeout(() => {
            navigate(isDoctor ? '/doctor/appointments' : '/user/bookings');
          }, 3000);
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [timeUntilStart, timeRemaining, isDoctor, navigate]);

  const getMinutesAndSeconds = (seconds) => {
    const totalSecs = Math.max(0, seconds);
    const mins = Math.floor(totalSecs / 60);
    const secs = Math.floor(totalSecs % 60);
    return {
      minutes: String(mins).padStart(2, '0'),
      seconds: String(secs).padStart(2, '0'),
    };
  };

  const formatScheduledDate = (isoString) => {
    if (!isoString) return 'Fri, 24 Jul, 04:00 pm';
    const date = new Date(isoString);
    const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const timeStr = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }).toLowerCase();
    return `${dayName}, ${day} ${month}, ${timeStr}`;
  };

  const handleJoinCall = useCallback(() => {
    navigate(isDoctor ? `/doctor/video-call/${appointmentId}` : `/user/video-call/${appointmentId}`);
  }, [appointmentId, isDoctor, navigate]);

  const handleGoBack = () => {
    navigate(isDoctor ? '/doctor/appointments' : '/user/bookings');
  };

  const handleContactSupport = () => {
    window.location.href = 'mailto:support@fitnova.com?subject=Waiting Room Support';
  };

  const handleExploreDoctors = () => {
    navigate(isDoctor ? '/doctor/dashboard' : '/user/doctor_list');
  };

  const handleTestMedia = async (type) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: type === 'camera' || type === 'both',
        audio: type === 'mic' || type === 'both',
      });
      if (type === 'both') {
        toast.success('Camera and microphone are working!');
      } else {
        toast.success(`${type === 'camera' ? 'Camera' : 'Microphone'} is working perfectly!`);
      }
      stream.getTracks().forEach(track => track.stop());
    } catch (err) {
      toast.error(`Permission denied or device not found.`);
    }
  };

  const formatAppointmentWhen = (isoString) => {
    if (!isoString) return '—';
    const date = new Date(isoString);
    const now = new Date();
    const isToday =
      date.getDate() === now.getDate() &&
      date.getMonth() === now.getMonth() &&
      date.getFullYear() === now.getFullYear();
    const timeStr = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    if (isToday) return `Today, ${timeStr}`;
    const day = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `${day}, ${timeStr}`;
  };

  const { minutes, seconds } = getMinutesAndSeconds(timeUntilStart > 0 ? timeUntilStart : timeRemaining);

  const isAudioCall = sessionInfo?.call_type === 'AUDIO';
  const participantName = sessionInfo?.other_participant?.name || (isDoctor ? 'John Smith' : 'Dr. Olivia Davis');
  const participantSpecialty =
    sessionInfo?.other_participant?.specialization ||
    sessionInfo?.other_participant?.qualification ||
    (isDoctor ? 'Patient' : 'General Physician');
  const participantImage = sessionInfo?.other_participant?.image || null;
  const slotDateStr = formatScheduledDate(sessionInfo?.slot_start);
  const appointmentWhenStr = formatAppointmentWhen(sessionInfo?.slot_start);
  const durationMin = sessionInfo?.slot_duration ?? 30;
  const joinEnabled = canJoin || sessionActive;

  const DEFAULT_HEALTH_TIPS = [
    {
      title: 'Stay Hydrated',
      description: 'Drink at least 8 glasses of water daily.',
      icon: 'bi-droplet-fill',
      bg: '#E8F1FF',
      color: '#2563EB',
    },
    {
      title: 'Eat Healthy',
      description: 'Include more fruits, vegetables and whole grains in your diet.',
      icon: 'bi-basket2-fill',
      bg: '#E8F8EF',
      color: '#16A34A',
    },
    {
      title: 'Stay Active',
      description: 'Do at least 30 minutes of exercise or walking daily.',
      icon: 'bi-person-walking',
      bg: '#FFF1E8',
      color: '#EA580C',
    },
    {
      title: 'Sleep Well',
      description: 'Get 7-8 hours of quality sleep every night.',
      icon: 'bi-moon-stars-fill',
      bg: '#F3E8FF',
      color: '#7C3AED',
    },
    {
      title: 'Manage Stress',
      description: 'Practice meditation or deep breathing for better mental well-being.',
      icon: 'bi-flower1',
      bg: '#FCE7F3',
      color: '#DB2777',
    },
  ];

  // Admin HealthContent → sidebar tips (POST preferred, then any)
  const sidebarTips = (() => {
    const adminItems = (healthContents.length > 0 ? healthContents : allHealthContents)
      .filter((c) => c && (c.title || c.description));
    const posts = adminItems.filter((c) => c.content_type === 'POST');
    const source = (posts.length > 0 ? posts : adminItems).slice(0, 5);
    if (source.length === 0) return DEFAULT_HEALTH_TIPS;
    return source.map((c, i) => {
      const style = TIP_STYLES[i % TIP_STYLES.length];
      return {
        id: c.id || i,
        title: c.title,
        description: c.description || 'Stay healthy with Fitnova.',
        icon: style.icon,
        bg: style.bg,
        color: style.color,
        image: c.image,
      };
    });
  })();

  const tipsFromAdmin = healthContents.length > 0 || (sessionInfo?.healthcare_posts?.length > 0);

  const doctorDisplayName = (() => {
    const n = participantName || 'Doctor';
    if (n.startsWith('Dr')) return n;
    return `Dr. ${n}`;
  })();

  const doctorQualification = sessionInfo?.other_participant?.qualification || '';
  const doctorSpecialty = sessionInfo?.other_participant?.specialization || participantSpecialty || 'General Physician';

  const doctorAvatarUrl = resolveMediaUrl(participantImage) ||
    `https://ui-avatars.com/api/?name=${encodeURIComponent(participantName || 'Doctor')}&background=2563EB&color=fff`;

  const appointmentCode = `FN-${String(appointmentId || '').toUpperCase()}`;


  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
        {isDoctor ? <DoctorNavbar /> : <UserNavbar />}
        <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '70vh' }}>
          <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-muted fw-semibold">Preparing your consultation waiting room...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
        {isDoctor ? <DoctorNavbar /> : <UserNavbar />}
        <div className="d-flex flex-column align-items-center justify-content-center text-center p-4" style={{ minHeight: '70vh' }}>
          <div className="bg-danger bg-opacity-10 text-danger rounded-circle p-4 mb-3">
            <i className="bi bi-calendar-x fs-1"></i>
          </div>
          <h3 className="fw-bold text-dark mb-2">Unable to join session</h3>
          <p className="text-muted mb-4" style={{ maxWidth: 400 }}>{error}</p>
          <button type="button" onClick={handleGoBack} className="btn btn-primary px-4 py-2 rounded-3 fw-semibold">
            <i className="bi bi-arrow-left me-2"></i> Go back
          </button>
        </div>
      </div>
    );
  }

  /* ========================================================================= */
  /* DOCTOR WAITING ROOM VIEW */
  /* ========================================================================= */
  if (isDoctor) {
    const patientForLabel =
      sessionInfo?.other_participant?.for_label ||
      (sessionInfo?.other_participant?.relation
        ? `For ${sessionInfo.other_participant.name} (${sessionInfo.other_participant.relation})`
        : 'For Self');
    const startEnabled = canJoin || timeUntilStart <= 0;
    const patientAvatarUrl =
      resolveMediaUrl(participantImage) ||
      `https://ui-avatars.com/api/?name=${encodeURIComponent(participantName || 'Patient')}&background=E2E8F0&color=64748B`;

    return (
      <div
        style={{
          height: '100dvh',
          maxHeight: '100dvh',
          overflow: 'hidden',
          backgroundColor: '#F4F7FB',
          fontFamily: "'Segoe UI', 'Nunito Sans', system-ui, sans-serif",
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div className="flex-shrink-0">
          <DoctorNavbar />
        </div>

        <div
          className="flex-grow-1 d-flex flex-column justify-content-center"
          style={{ overflow: 'hidden', minHeight: 0, padding: '10px 16px 14px' }}
        >
          {/* Single white card — all waiting room content */}
          <div
            className="mx-auto w-100"
            style={{
              maxWidth: 640,
              background: '#FFFFFF',
              borderRadius: 16,
              border: '1px solid #E8EDF5',
              boxShadow: '0 4px 24px rgba(15, 23, 42, 0.06)',
              padding: '16px 20px 14px',
            }}
          >
            <button
              type="button"
              className="btn btn-link text-decoration-none p-0 mb-2 fw-semibold d-inline-flex align-items-center gap-1"
              style={{ color: '#2563EB', fontSize: 13 }}
              onClick={handleGoBack}
            >
              <i className="bi bi-arrow-left" />
              Back
            </button>

            <h2 className="fw-bold text-dark mb-0" style={{ fontSize: '1.4rem', letterSpacing: '-0.03em' }}>
              Waiting Room
            </h2>
            <p className="text-secondary mb-3" style={{ fontSize: 13 }}>
              Your patient will join shortly.
            </p>

            {/* Patient + appointment */}
            <div
              className="rounded-3 px-3 py-2 mb-2"
              style={{ background: '#F8FAFC', border: '1px solid #EEF2F7' }}
            >
              <div className="d-flex flex-column flex-md-row align-items-start justify-content-between gap-2">
                <div className="d-flex align-items-center gap-2">
                  <img
                    src={patientAvatarUrl}
                    alt={participantName}
                    className="rounded-circle flex-shrink-0"
                    style={{ width: 42, height: 42, objectFit: 'cover', border: '1px solid #E2E8F0' }}
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(participantName || 'Patient')}&background=E2E8F0&color=64748B`;
                    }}
                  />
                  <div>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <h6 className="fw-bold text-dark mb-0" style={{ fontSize: 15 }}>
                        {participantName}
                      </h6>
                      <span
                        className="badge rounded-pill fw-semibold"
                        style={{ background: '#E8F8EF', color: '#15803D', fontSize: 10, padding: '3px 8px' }}
                      >
                        Patient
                      </span>
                    </div>
                    <p className="text-secondary mb-0" style={{ fontSize: 12 }}>
                      {patientForLabel}
                    </p>
                  </div>
                </div>

                <div className="d-flex flex-column gap-1" style={{ minWidth: 180 }}>
                  <div className="d-flex align-items-center gap-2" style={{ fontSize: 12, color: '#1E293B' }}>
                    <i className="bi bi-calendar3" style={{ color: '#64748B', width: 16 }} />
                    <span className="fw-medium">{appointmentWhenStr}</span>
                  </div>
                  <div className="d-flex align-items-center gap-2" style={{ fontSize: 12, color: '#1E293B' }}>
                    <i className={`bi ${isAudioCall ? 'bi-telephone' : 'bi-camera-video'}`} style={{ color: '#64748B', width: 16 }} />
                    <span className="fw-medium">{isAudioCall ? 'Audio Consultation' : 'Video Consultation'}</span>
                  </div>
                  <div className="d-flex align-items-center gap-2" style={{ fontSize: 11, color: '#64748B' }}>
                    <i className="bi bi-file-earmark-text" style={{ width: 16 }} />
                    <span>Appointment ID {appointmentCode}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Status + timer */}
            <div
              className="rounded-3 px-3 py-2 mb-2"
              style={{ background: '#F8FAFC', border: '1px solid #EEF2F7' }}
            >
              <div className="d-flex justify-content-between align-items-start gap-3 mb-2">
                <div>
                  <p className="text-secondary mb-1" style={{ fontSize: 11 }}>Status</p>
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <span
                      className="rounded-circle flex-shrink-0"
                      style={{ width: 8, height: 8, background: '#22C55E' }}
                    />
                    <span className="fw-semibold text-dark" style={{ fontSize: 13 }}>
                      {startEnabled ? 'Ready to Start' : 'Waiting for Patient'}
                    </span>
                  </div>
                  <p className="text-secondary mb-0 d-flex align-items-center gap-1" style={{ fontSize: 11 }}>
                    <i className="bi bi-person-hearts" style={{ color: '#94A3B8' }} />
                    You can start when the patient joins.
                  </p>
                </div>
                <div className="text-end flex-shrink-0">
                  <p className="text-secondary mb-0" style={{ fontSize: 11 }}>Starts In</p>
                  <div className="fw-bold" style={{ fontSize: '1.75rem', color: '#16A34A', lineHeight: 1.1 }}>
                    {minutes}:{seconds}
                  </div>
                  <p className="text-secondary mb-0" style={{ fontSize: 11 }}>Minutes</p>
                </div>
              </div>

              <div
                className="d-flex align-items-center gap-2 rounded-3 px-2 py-2"
                style={{ background: '#EBF3FF' }}
              >
                <div
                  className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                  style={{ width: 22, height: 22, background: '#2563EB', color: '#fff', fontSize: 11 }}
                >
                  i
                </div>
                <p className="mb-0" style={{ fontSize: 11, color: '#1E3A5F', lineHeight: 1.35 }}>
                  The patient will join shortly. Please stay on this page.
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="mb-2 d-flex flex-column align-items-center">
              <button
                type="button"
                className="btn py-2 px-4 rounded-3 fw-semibold d-flex align-items-center justify-content-center gap-2"
                disabled={!startEnabled}
                onClick={handleJoinCall}
                style={{
                  background: '#2563EB',
                  color: '#fff',
                  border: 'none',
                  fontSize: 13,
                  minWidth: 220,
                  opacity: startEnabled ? 1 : 0.45,
                  cursor: startEnabled ? 'pointer' : 'not-allowed',
                }}
              >
                <i className={`bi ${isAudioCall ? 'bi-telephone-fill' : 'bi-camera-video-fill'}`} />
                Start Consultation
              </button>
              {!startEnabled && (
                <p className="text-center text-secondary mb-0 mt-1" style={{ fontSize: 11 }}>
                  This button will be enabled when the patient joins.
                </p>
              )}
            </div>

            <p className="text-center text-secondary mb-0" style={{ fontSize: 11 }}>
              Stay prepared. Great consultations start with a calm mind.
            </p>
          </div>
        </div>
      </div>
    );
  }

  /* ========================================================================= */
  /* USER WAITING ROOM VIEW */
  /* ========================================================================= */
  return (
    <div
      style={{
        height: '100dvh',
        maxHeight: '100dvh',
        overflow: 'hidden',
        backgroundColor: '#F4F7FB',
        fontFamily: "'Segoe UI', 'Nunito Sans', system-ui, sans-serif",
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div className="flex-shrink-0">
        <UserNavbar />
      </div>

      <div
        className="flex-grow-1 d-flex align-items-center"
        style={{ overflow: 'hidden', minHeight: 0, padding: '10px 16px 12px' }}
      >
        <div className="container-fluid w-100" style={{ maxWidth: 1080 }}>
          <div className="row g-3 align-items-stretch">

            {/* Main waiting card */}
            <div className="col-12 col-lg-8">
              <div
                className="h-100"
                style={{
                  background: '#FFFFFF',
                  borderRadius: 16,
                  border: '1px solid #E8EDF5',
                  boxShadow: '0 4px 24px rgba(15, 23, 42, 0.06)',
                  padding: '14px 18px 12px',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <button
                  type="button"
                  className="btn btn-link text-decoration-none p-0 mb-2 fw-semibold d-inline-flex align-items-center gap-1 align-self-start"
                  style={{ color: '#2563EB', fontSize: 13 }}
                  onClick={handleGoBack}
                >
                  <i className="bi bi-arrow-left" />
                  Back
                </button>

                <h2 className="fw-bold text-dark mb-0" style={{ fontSize: '1.35rem', letterSpacing: '-0.03em' }}>
                  Waiting for the Doctor
                </h2>
                <p className="text-secondary mb-3" style={{ fontSize: 13 }}>
                  Your doctor will join the consultation shortly.
                </p>

                {/* Doctor info */}
                <div
                  className="rounded-3 px-3 py-2 mb-2"
                  style={{ background: '#F8FAFC', border: '1px solid #EEF2F7' }}
                >
                  <div className="d-flex flex-column flex-md-row align-items-start justify-content-between gap-2">
                    <div className="d-flex align-items-center gap-2 min-w-0">
                      <img
                        src={doctorAvatarUrl}
                        alt={doctorDisplayName}
                        className="rounded-circle flex-shrink-0"
                        style={{ width: 48, height: 48, objectFit: 'cover', border: '1px solid #E2E8F0' }}
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(participantName || 'Doctor')}&background=2563EB&color=fff`;
                        }}
                      />
                      <div className="min-w-0">
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <h6 className="fw-bold text-dark mb-0 text-truncate" style={{ fontSize: 15 }}>
                            {doctorDisplayName}
                          </h6>
                          <span
                            className="badge rounded-pill fw-semibold"
                            style={{ background: '#E8F8EF', color: '#15803D', fontSize: 10, padding: '3px 8px' }}
                          >
                            {doctorSpecialty}
                          </span>
                        </div>
                        <p className="text-secondary mb-0" style={{ fontSize: 12 }}>
                          {doctorQualification || doctorSpecialty}
                        </p>
                      </div>
                    </div>

                    <div className="d-flex flex-column gap-1" style={{ minWidth: 180 }}>
                      <div className="d-flex align-items-center gap-2" style={{ fontSize: 12, color: '#1E293B' }}>
                        <i className="bi bi-calendar3" style={{ color: '#64748B', width: 16 }} />
                        <span className="fw-medium">{appointmentWhenStr}</span>
                      </div>
                      <div className="d-flex align-items-center gap-2" style={{ fontSize: 12, color: '#1E293B' }}>
                        <i className={`bi ${isAudioCall ? 'bi-telephone' : 'bi-camera-video'}`} style={{ color: '#64748B', width: 16 }} />
                        <span className="fw-medium">{isAudioCall ? 'Audio Consultation' : 'Video Consultation'}</span>
                      </div>
                      <div className="d-flex align-items-center gap-2" style={{ fontSize: 11, color: '#64748B' }}>
                        <i className="bi bi-file-earmark-text" style={{ width: 16 }} />
                        <span>Appointment ID {appointmentCode}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Status + timer */}
                <div
                  className="rounded-3 px-3 py-2 mb-2"
                  style={{ background: '#F8FAFC', border: '1px solid #EEF2F7' }}
                >
                  <div className="d-flex justify-content-between align-items-start gap-3 mb-2">
                    <div>
                      <p className="text-secondary mb-1" style={{ fontSize: 11 }}>Status</p>
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <span
                          className="rounded-circle flex-shrink-0"
                          style={{ width: 8, height: 8, background: '#22C55E' }}
                        />
                        <span className="fw-semibold text-dark" style={{ fontSize: 13 }}>
                          {joinEnabled ? 'Doctor Ready' : 'Waiting for the Doctor'}
                        </span>
                      </div>
                    </div>
                    <div className="text-end flex-shrink-0">
                      <p className="text-secondary mb-0" style={{ fontSize: 11 }}>Starts In</p>
                      <div className="fw-bold" style={{ fontSize: '1.75rem', color: '#16A34A', lineHeight: 1.1 }}>
                        {minutes}:{seconds}
                      </div>
                      <p className="text-secondary mb-0" style={{ fontSize: 11 }}>Minutes</p>
                    </div>
                  </div>

                  <div
                    className="d-flex align-items-center gap-2 rounded-3 px-2 py-2"
                    style={{ background: '#EBF3FF' }}
                  >
                    <div
                      className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                      style={{ width: 22, height: 22, background: '#2563EB', color: '#fff', fontSize: 11 }}
                    >
                      i
                    </div>
                    <p className="mb-0" style={{ fontSize: 11, color: '#1E3A5F', lineHeight: 1.35 }}>
                      The doctor will join the consultation shortly. Please stay on this page.
                    </p>
                  </div>
                </div>

                <div className="mt-auto d-flex flex-column align-items-center pt-2">
                  <button
                    type="button"
                    className="btn py-2 px-4 rounded-3 fw-semibold d-flex align-items-center justify-content-center gap-2"
                    disabled={!joinEnabled}
                    onClick={handleJoinCall}
                    style={{
                      background: '#2563EB',
                      color: '#fff',
                      border: 'none',
                      fontSize: 13,
                      minWidth: 220,
                      opacity: joinEnabled ? 1 : 0.45,
                      cursor: joinEnabled ? 'pointer' : 'not-allowed',
                    }}
                  >
                    <i className={`bi ${isAudioCall ? 'bi-telephone-fill' : 'bi-camera-video-fill'}`} />
                    Start Consultation
                  </button>
                  {!joinEnabled && (
                    <p className="text-center text-secondary mb-0 mt-1" style={{ fontSize: 11 }}>
                      This button will be enabled when the doctor joins.
                    </p>
                  )}
                  <p className="text-center text-secondary mb-0 mt-2" style={{ fontSize: 11 }}>
                    Stay prepared. Great consultations start with a calm mind.
                  </p>
                </div>
              </div>
            </div>

            {/* Health Tips — from admin HealthContent */}
            <div className="col-12 col-lg-4 d-none d-lg-block">
              <div
                className="h-100 d-flex flex-column"
                style={{
                  background: '#FFFFFF',
                  borderRadius: 16,
                  border: '1px solid #E8EDF5',
                  boxShadow: '0 4px 24px rgba(15, 23, 42, 0.06)',
                  padding: '14px 16px 12px',
                }}
              >
                <h5 className="fw-bold text-dark mb-3" style={{ fontSize: 16 }}>Health Tips</h5>

                {healthLoading ? (
                  <div className="d-flex justify-content-center py-4">
                    <div className="spinner-border spinner-border-sm text-primary" role="status" />
                  </div>
                ) : (
                  <div className="d-flex flex-column gap-2 flex-grow-1" style={{ overflow: 'hidden' }}>
                    {sidebarTips.map((tip, idx) => (
                      <div key={tip.id || tip.title || idx} className="d-flex align-items-start gap-2">
                        {tip.image ? (
                          <img
                            src={resolveMediaUrl(tip.image)}
                            alt=""
                            className="rounded-3 flex-shrink-0"
                            style={{ width: 36, height: 36, objectFit: 'cover' }}
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          />
                        ) : (
                          <div
                            className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
                            style={{ width: 36, height: 36, background: tip.bg, color: tip.color }}
                          >
                            <i className={`bi ${tip.icon}`} style={{ fontSize: 15 }} />
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="fw-bold text-dark mb-0" style={{ fontSize: 13 }}>{tip.title}</p>
                          <p className="text-secondary mb-0" style={{ fontSize: 11, lineHeight: 1.35 }}>
                            {tip.description}
                          </p>
                        </div>
                      </div>
                    ))}
                    {!tipsFromAdmin && (
                      <p className="text-muted mb-0 mt-1" style={{ fontSize: 10 }}>
                        Add tips in Django Admin → Health contents
                      </p>
                    )}
                  </div>
                )}
                {healthError && !healthLoading && (
                  <p className="text-danger small mb-0 mt-1" style={{ fontSize: 11 }}>{healthError}</p>
                )}

                <div
                  className="d-flex align-items-center gap-2 rounded-3 px-2 py-2 mt-3"
                  style={{ background: '#EBF3FF' }}
                >
                  <div
                    className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                    style={{ width: 28, height: 28, background: '#2563EB', color: '#fff' }}
                  >
                    <i className="bi bi-heart-pulse-fill" style={{ fontSize: 12 }} />
                  </div>
                  <p className="mb-0" style={{ fontSize: 11, color: '#1E3A5F', lineHeight: 1.35 }}>
                    Good health is the greatest wealth. Take care, stay healthy!
                  </p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}

export default WaitingRoom;
